/**
 * The cut cookie, server side.
 *
 * The visitor's cut chooser (src/components/chrome/cut-chooser.tsx) writes
 * `dcyfr-cut=<cut>` when a cut is picked. The root layout reads it on every
 * request and stamps `data-cut` on <html> so the chosen cut renders before
 * the first paint, with no client re-stamp and no flash of the base cut.
 *
 * What is stamped, per design decision 4 of dcyfr-obsidian-identity:
 *   - a declared, non-base cut is stamped as its own name;
 *   - the base cut, an unknown value, an empty value and a missing cookie
 *     all stamp nothing. The engine renders the base cut when the attribute
 *     is absent, when it names the base and when it names a cut the
 *     identity does not declare, and the chooser REMOVES the attribute when
 *     the base is picked, so absence is the one canonical form for the base
 *     and the served HTML and the live DOM agree.
 *   - a retired cut name in RETIRED_CUT_ALIASES reads as the cut it was
 *     renamed to, so a visitor who picked it keeps that cut. The cookie is
 *     not rewritten; the next pick in the chooser replaces it.
 *
 * The cookie name lives here rather than being imported from the chooser:
 * that file is a "use client" module, and a server component importing a
 * constant from it would get a client reference, not the string.
 */

import { isThemeCut, themeCuts, type ThemeCut } from '@/lib/theme-cuts';

/** Must match DEFAULT_CUT_COOKIE in src/components/chrome/cut-chooser.tsx. */
export const CUT_COOKIE = 'dcyfr-cut';

/**
 * Cut names a stored cookie may still carry after a rename, mapped to the
 * cut that replaced them. Matched exactly, like a declared cut: 'FIRE' or
 * ' fire' is still unknown.
 *
 * 2026-10-01: obsidian went from six cuts to five. The old rainbow cut was
 * dropped and fire was renamed rainbow, keeping fire's ramp and sheen, so a
 * stored 'fire' reads as 'rainbow'. The cookie lives a year (Max-Age in the
 * chooser), so this alias can go once a year has passed since that change
 * shipped. A stored 'rainbow' already names the new rainbow.
 */
const RETIRED_CUT_ALIASES: Readonly<Record<string, ThemeCut>> = { fire: 'rainbow' };

/**
 * The `data-cut` value to stamp for a cookie value, or undefined for none.
 * Undefined is what React needs to omit the attribute entirely.
 */
export function resolveCutFromCookie(value: string | undefined | null): ThemeCut | undefined {
  const named =
    typeof value === 'string' && Object.prototype.hasOwnProperty.call(RETIRED_CUT_ALIASES, value)
      ? RETIRED_CUT_ALIASES[value]
      : value;
  if (!isThemeCut(named)) return undefined;
  return named === themeCuts.base ? undefined : named;
}

/**
 * The cut a `document.cookie` string names, resolved the way the server
 * resolves it: a declared non-base cut, null for the base or an unknown
 * value, and undefined when there is no cut cookie at all. The design
 * showcase reads this on unmount to put the visitor's cut back.
 *
 * The chooser writes the value percent-encoded, so it is decoded here. A
 * value that is not valid percent-encoding (a lone `%`, a truncated `%E0`)
 * counts as unknown and is never thrown on: this runs inside a React
 * cleanup, where an exception is a client-side crash on every exit from the
 * page. Next's own cookie parser drops such a value before the layout sees
 * it, so the server stamps nothing for it, which is what null means here.
 */
export function readCutCookie(cookieString: string): ThemeCut | null | undefined {
  const prefix = `${CUT_COOKIE}=`;
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (match === undefined) return undefined;
  let value = match.slice(prefix.length);
  try {
    value = decodeURIComponent(value);
  } catch {
    return null;
  }
  return resolveCutFromCookie(value) ?? null;
}
